import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { 
  User, 
  Mail, 
  Phone, 
  MessageSquare,
  Bell,
  Smartphone,
  MessageCircle,
  Shield,
  Lock
} from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { useFeatureFlag } from '../../context/FeatureFlagContext';
import { formatUSPhone, isValidUSPhone } from '../../utils/phoneUtils';

export const ContactPreferencesStep = ({ formData, updateFormData, validationErrors = {}, isAuthenticated }) => {
  const isAutoRegistrationEnabled = useFeatureFlag('auto_registration_enabled');
  const contactMethods = [
    {
      value: 'email',
      label: 'Email Only',
      description: 'Receive all communications via email',
      icon: Mail,
      recommended: false
    },
    {
      value: 'phone',
      label: 'Phone Only',
      description: 'Prefer phone calls for communication',
      icon: Phone,
      recommended: false
    },
    {
      value: 'both',
      label: 'Both Email & Phone',
      description: 'Email for updates, phone for urgent matters',
      icon: MessageSquare,
      recommended: true
    }
  ];

  const handleInputChange = (field, value) => {
    updateFormData({ [field]: value });
  };

  const handleContactMethodSelect = (method) => {
    updateFormData({ contactMethod: method });
    setTimeout(() => {
      document.getElementById('notification-settings')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
  };

  const handleNotificationChange = (notificationType, value) => {
    updateFormData({
      notifications: {
        ...formData.notifications,
        [notificationType]: value
      }
    });
  };

  return (
    <div className="space-y-8 mb-12">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Contact Details & Preferences
        </h3>
        <p className="text-gray-600">
          Help service providers reach you and customize your notification settings
        </p>
      </div>

      {/* Contact Information */}
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <User className="w-5 h-5 text-emerald-600" />
          <h4 className="text-lg font-semibold text-gray-900">Contact Information</h4>
        </div>

        {/* First and Last Name */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="firstName" className="text-base font-medium text-gray-900">
              First Name *
            </Label>
            {validationErrors.contact_first_name && (
              <p className="text-sm text-red-600 font-medium">{validationErrors.contact_first_name}</p>
            )}
            <Input
              id="firstName"
              placeholder="Your first name"
              value={formData.contact_first_name}
              onChange={(e) => handleInputChange('contact_first_name', e.target.value)}
              className={`h-12 text-base border-2 focus:border-emerald-500 transition-colors duration-200 ${
                validationErrors.contact_first_name ? 'border-red-500 focus:border-red-500' : ''
              }`}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName" className="text-base font-medium text-gray-900">
              Last Name *
            </Label>
            {validationErrors.contact_last_name && (
              <p className="text-sm text-red-600 font-medium">{validationErrors.contact_last_name}</p>
            )}
            <Input
              id="lastName"
              placeholder="Your last name"
              value={formData.contact_last_name}
              onChange={(e) => handleInputChange('contact_last_name', e.target.value)}
              className={`h-12 text-base border-2 focus:border-emerald-500 transition-colors duration-200 ${
                validationErrors.contact_last_name ? 'border-red-500 focus:border-red-500' : ''
              }`}
            />
          </div>
        </div>

        {/* Email & Phone Row */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-base font-medium text-gray-900">
              Email Address *
            </Label>
            <div className="relative">
              {validationErrors.contact_email && (
                <p className="text-sm text-red-600 font-medium mb-2">{validationErrors.contact_email}</p>
              )}
              <Input
                id="email"
                type="email"
                placeholder="your.email@example.com"
                value={formData.contact_email}
                onChange={(e) => handleInputChange('contact_email', e.target.value)}
                disabled={isAuthenticated}
                className={`h-12 text-base border-2 focus:border-emerald-500 transition-colors duration-200 pl-12 ${
                  validationErrors.contact_email ? 'border-red-500 focus:border-red-500' : ''
                } ${isAuthenticated ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              />
              <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              {isAuthenticated && (
                <p className="text-xs text-gray-500 mt-1">Email is linked to your account and cannot be changed here.</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="text-base font-medium text-gray-900">
              Phone Number *
            </Label>
            <div className="relative">
              {validationErrors.contact_phone && (
                <p className="text-sm text-red-600 font-medium mb-2">{validationErrors.contact_phone}</p>
              )}
              <Input
                id="phone"
                type="tel"
                placeholder="+1 (555) 000-0000"
                value={formData.contact_phone}
                onChange={(e) => handleInputChange('contact_phone', formatUSPhone(e.target.value))}
                onBlur={() => {
                  if (formData.contact_phone && !isValidUSPhone(formData.contact_phone)) {
                    handleInputChange('contact_phone', formData.contact_phone); // trigger re-validate
                  }
                }}
                maxLength={17}
                className={`h-12 text-base border-2 focus:border-emerald-500 transition-colors duration-200 pl-12 ${
                  validationErrors.contact_phone ? 'border-red-500 focus:border-red-500' : ''
                }`}
              />
              <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Account Creation (Only for non-authenticated users and if enabled) */}
      {!isAuthenticated && isAutoRegistrationEnabled && (
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4">
          <div className="flex items-start space-x-2">
            <Checkbox 
              id="create-account" 
              checked={formData.createAccount}
              onCheckedChange={(checked) => updateFormData({ createAccount: checked })}
              className="mt-1"
            />
            <div className="grid gap-1.5 leading-none">
              <Label 
                htmlFor="create-account" 
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                Create an account for faster bookings
              </Label>
              <p className="text-xs text-gray-500">
                Save your details to track this project and book future jobs easily.
              </p>
            </div>
          </div>

          {formData.createAccount && (
            <div className="ml-6 animate-in slide-in-from-top-2 duration-200">
              <Label htmlFor="password" className="text-sm font-medium text-gray-900">
                Create Password *
              </Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  value={formData.password || ''}
                  onChange={(e) => updateFormData({ password: e.target.value })}
                  placeholder="Min. 6 characters"
                  className={`pl-10 h-12 text-base border-2 focus:border-emerald-500 transition-colors duration-200 ${
                    validationErrors.password ? 'border-red-500 focus:border-red-500' : ''
                  }`}
                />
              </div>
              {validationErrors.password && (
                <p className="text-sm text-red-600 font-medium mt-1">{validationErrors.password}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Communication Preferences */}
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <MessageCircle className="w-5 h-5 text-emerald-600" />
          <h4 className="text-lg font-semibold text-gray-900">Communication Preferences</h4>
        </div>

        <div className="space-y-2">
          <Label className="text-base font-medium text-gray-900">
            How would you like service providers to contact you? *
          </Label>
          <div className="space-y-3">
            {contactMethods.map((method) => (
              <Card
                key={method.value}
                className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
                  formData.contactMethod === method.value
                    ? 'ring-2 ring-emerald-500 bg-emerald-50 border-emerald-200'
                    : 'hover:border-gray-300'
                }`}
                onClick={() => handleContactMethodSelect(method.value)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${
                        formData.contactMethod === method.value
                          ? 'bg-emerald-100'
                          : 'bg-gray-100'
                      }`}>
                        <method.icon className={`w-5 h-5 ${
                          formData.contactMethod === method.value
                            ? 'text-emerald-600'
                            : 'text-gray-600'
                        }`} />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="font-semibold text-gray-900">
                            {method.label}
                          </h4>
                          {method.recommended && (
                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
                              Recommended
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          {method.description}
                        </p>
                      </div>
                    </div>
                    {formData.contactMethod === method.value && (
                      <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div id="notification-settings" className="space-y-6">
        <div className="flex items-center space-x-2">
          <Bell className="w-5 h-5 text-emerald-600" />
          <h4 className="text-lg font-semibold text-gray-900">Notification Settings</h4>
        </div>

        <Card className="border-gray-200">
          <CardContent className="p-6 space-y-6">
            {/* Bid Received Notifications */}
            <div className="flex items-center justify-between">
              <div className="flex items-start space-x-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <Smartphone className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">
                    New Bid Notifications
                  </h5>
                  <p className="text-sm text-gray-600">
                    Get notified immediately when service providers submit bids
                  </p>
                </div>
              </div>
              <Switch
                checked={formData.notifications.bidReceived}
                onCheckedChange={(value) => handleNotificationChange('bidReceived', value)}
              />
            </div>

            {/* Message Notifications */}
            <div className="flex items-center justify-between">
              <div className="flex items-start space-x-3">
                <div className="bg-emerald-100 p-2 rounded-lg">
                  <MessageSquare className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">
                    Message Notifications
                  </h5>
                  <p className="text-sm text-gray-600">
                    Receive alerts for messages from service providers
                  </p>
                </div>
              </div>
              <Switch
                checked={formData.notifications.messages}
                onCheckedChange={(value) => handleNotificationChange('messages', value)}
              />
            </div>

            {/* Project Updates */}
            <div className="flex items-center justify-between">
              <div className="flex items-start space-x-3">
                <div className="bg-yellow-100 p-2 rounded-lg">
                  <Bell className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 mb-1">
                    Project Updates
                  </h5>
                  <p className="text-sm text-gray-600">
                    Updates about your project status and milestones
                  </p>
                </div>
              </div>
              <Switch
                checked={formData.notifications.updates}
                onCheckedChange={(value) => handleNotificationChange('updates', value)}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Privacy Information */}
      <Card className="bg-gray-50 border-gray-200">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <Shield className="w-5 h-5 text-gray-600 mt-0.5" />
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">
                Your Privacy Matters
              </h5>
              <p className="text-sm text-gray-600">
                Your contact information will only be shared with service providers you choose to work with. 
                We never sell your information and you can update these preferences anytime.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};